import type { 
  RefPartner, 
  Staff, 
  Stakeholder, 
  Referral, 
  CommissionRule, 
  CommissionEntry, 
  Payout,
  ReferralPolicy
} from '../types';
import { generateReferralCode, buildReferralLink, generateQRPlaceholderUrl } from '../utils/referralUtils';

class MockReferralDB {
  private storageKey = 'referral_data';

  private data = {
    partners: [] as RefPartner[],
    staff: [] as Staff[],
    stakeholders: [] as Stakeholder[],
    referrals: [] as Referral[],
    commissionRules: [] as CommissionRule[],
    commissionEntries: [] as CommissionEntry[],
    payouts: [] as Payout[],
    policies: [] as ReferralPolicy[],
  };

  constructor() {
    this.loadFromStorage();
    this.seedInitialData();
  }

  private loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.data = { ...this.data, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.warn('Failed to load referral data from localStorage:', error);
    }
  }

  private saveToStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.data));
    } catch (error) {
      console.warn('Failed to save referral data to localStorage:', error);
    }
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private seedInitialData() {
    if (this.data.partners.length === 0) {
      const now = new Date().toISOString();
      
      const partners: RefPartner[] = [
        {
          id: 'partner-1',
          role: 'pharmacy',
          name: 'City Pharmacy',
          contactPerson: 'John Doe',
          phone: '+1234567890',
          email: 'contact@citypharmacy.com',
          address: '123 Main St, City',
          active: true,
          referralCode: 'RP-8G3KQ4',
          referralLink: '/r/RP-8G3KQ4',
          qrUrl: generateQRPlaceholderUrl('RP-8G3KQ4'),
          createdAt: now,
          updatedAt: now,
        },
        {
          id: 'partner-2',
          role: 'clinic',
          name: 'Downtown Medical Clinic',
          contactPerson: 'Dr. Sarah Wilson',
          phone: '+1234567891',
          email: 'info@downtownmedical.com',
          address: '456 Oak Ave, Downtown',
          active: true,
          referralCode: 'RP-2M5N8P',
          referralLink: '/r/RP-2M5N8P',
          qrUrl: generateQRPlaceholderUrl('RP-2M5N8P'),
          createdAt: now,
          updatedAt: now,
        },
      ];

      const staff: Staff[] = [
        {
          id: 'staff-1',
          empCode: 'EMP001',
          name: 'Dr. Michael Johnson',
          phone: '+1234567892',
          email: 'mjohnson@hospital.com',
          department: 'Cardiology',
          role: 'doctor',
          active: true,
          referralCode: 'ST-9Q2M5K',
          referralLink: '/r/ST-9Q2M5K',
          qrUrl: generateQRPlaceholderUrl('ST-9Q2M5K'),
          createdAt: now,
          updatedAt: now,
        },
        {
          id: 'staff-2',
          empCode: 'EMP002',
          name: 'Lisa Chen',
          phone: '+1234567893',
          email: 'lchen@hospital.com',
          department: 'Reception',
          role: 'reception',
          active: true,
          referralCode: 'ST-7X4K9L',
          referralLink: '/r/ST-7X4K9L',
          qrUrl: generateQRPlaceholderUrl('ST-7X4K9L'),
          createdAt: now,
          updatedAt: now,
        },
      ];

      const stakeholders: Stakeholder[] = [
        {
          id: 'stakeholder-1',
          type: 'owner',
          name: 'Robert Smith',
          phone: '+1234567894',
          email: 'rsmith@hospital.com',
          equityPercent: 60,
          active: true,
          referralCode: 'SH-7X2NQ4',
          referralLink: '/r/SH-7X2NQ4',
          qrUrl: generateQRPlaceholderUrl('SH-7X2NQ4'),
          createdAt: now,
          updatedAt: now,
        },
      ];

      const referrals: Referral[] = [
        {
          id: 'referral-1',
          type: 'in',
          patientId: 'patient-1',
          patientName: 'Alice Brown',
          patientPhone: '+1234567895',
          consentGiven: true,
          source: {
            kind: 'external_partner',
            id: 'partner-1',
            code: 'RP-8G3KQ4',
          },
          reason: 'Routine checkup referral',
          notes: 'Patient needs general consultation',
          dateCreated: now,
          status: 'pending',
          estimatedAmount: 150,
        },
        {
          id: 'referral-2',
          type: 'in',
          patientId: 'patient-2',
          patientName: 'Bob Wilson',
          patientPhone: '+1234567896',
          consentGiven: true,
          source: {
            kind: 'internal_staff',
            id: 'staff-2',
            code: 'ST-7X4K9L',
          },
          reason: 'Follow-up appointment needed',
          notes: 'Patient requires specialist consultation',
          dateCreated: now,
          status: 'completed',
          estimatedAmount: 200,
        },
      ];

      const commissionRules: CommissionRule[] = [
        {
          id: 'rule-1',
          referrer: {
            kind: 'external_partner',
            id: 'partner-1',
            code: 'RP-8G3KQ4',
          },
          basis: 'percentage',
          value: 10,
          capPerReferral: 50,
          monthlyCap: 500,
          active: true,
          createdAt: now,
          updatedAt: now,
        },
        {
          id: 'rule-2',
          referrer: {
            kind: 'internal_staff',
            id: 'staff-2',
            code: 'ST-7X4K9L',
          },
          basis: 'flat',
          value: 25,
          active: true,
          createdAt: now,
          updatedAt: now,
        },
      ];

      const commissionEntries: CommissionEntry[] = [
        {
          id: 'entry-1',
          referralId: 'referral-2',
          referrer: {
            kind: 'internal_staff',
            id: 'staff-2',
            code: 'ST-7X4K9L',
          },
          calculatedAmount: 25,
          payoutStatus: 'unpaid',
          createdAt: now,
          updatedAt: now,
        },
      ];

      const policies: ReferralPolicy[] = [
        {
          id: 'policy-1',
          allowDoctorCommissions: false,
          allowStakeholderPayouts: false,
          blockSelfReferrals: true,
          enablePublicReferrals: true,
          publicReferralRateLimit: 5,
          enableDuplicateDetection: true,
          defaultPerReferralCap: 50,
          defaultMonthlyCap: 500,
          enablePartnerPortal: true,
          requirePartnerOTP: true,
          allowPartnerQRDownload: true,
          createdAt: now,
          updatedAt: now,
        },
      ];

      this.data.partners = partners;
      this.data.staff = staff;
      this.data.stakeholders = stakeholders;
      this.data.referrals = referrals;
      this.data.commissionRules = commissionRules;
      this.data.commissionEntries = commissionEntries;
      this.data.payouts = [];
      this.data.policies = policies;
      this.saveToStorage();
    }
  }

  getPartners(): RefPartner[] {
    return [...this.data.partners];
  }

  getPartner(id: string): RefPartner | undefined {
    return this.data.partners.find(p => p.id === id);
  }

  getPartnerByCode(code: string): RefPartner | undefined {
    return this.data.partners.find(p => p.referralCode === code);
  }

  createPartner(data: Omit<RefPartner, 'id' | 'referralCode' | 'referralLink' | 'qrUrl' | 'createdAt' | 'updatedAt'>): RefPartner {
    const now = new Date().toISOString();
    const referralCode = generateReferralCode('RP');
    const partner: RefPartner = {
      ...data,
      id: this.generateId(),
      referralCode,
      referralLink: buildReferralLink(referralCode),
      qrUrl: generateQRPlaceholderUrl(referralCode),
      createdAt: now,
      updatedAt: now,
    };
    this.data.partners.push(partner);
    this.saveToStorage();
    return partner;
  }

  updatePartner(id: string, data: Partial<Omit<RefPartner, 'id' | 'createdAt'>>): RefPartner | null {
    const index = this.data.partners.findIndex(p => p.id === id);
    if (index === -1) return null;

    this.data.partners[index] = {
      ...this.data.partners[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    this.saveToStorage();
    return this.data.partners[index];
  }

  getStaff(): Staff[] {
    return [...this.data.staff];
  }

  getStaffMember(id: string): Staff | undefined {
    return this.data.staff.find(s => s.id === id);
  }

  getStaffByCode(code: string): Staff | undefined {
    return this.data.staff.find(s => s.referralCode === code);
  }

  createStaff(data: Omit<Staff, 'id' | 'referralCode' | 'referralLink' | 'qrUrl' | 'createdAt' | 'updatedAt'>): Staff {
    const now = new Date().toISOString();
    const referralCode = generateReferralCode('ST');
    const staff: Staff = {
      ...data,
      id: this.generateId(),
      referralCode,
      referralLink: buildReferralLink(referralCode),
      qrUrl: generateQRPlaceholderUrl(referralCode),
      createdAt: now,
      updatedAt: now,
    };
    this.data.staff.push(staff);
    this.saveToStorage();
    return staff;
  }

  updateStaff(id: string, data: Partial<Omit<Staff, 'id' | 'createdAt'>>): Staff | null {
    const index = this.data.staff.findIndex(s => s.id === id);
    if (index === -1) return null;

    this.data.staff[index] = {
      ...this.data.staff[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    this.saveToStorage();
    return this.data.staff[index];
  }

  getStakeholders(): Stakeholder[] {
    return [...this.data.stakeholders];
  }

  getStakeholder(id: string): Stakeholder | undefined {
    return this.data.stakeholders.find(s => s.id === id);
  }

  getStakeholderByCode(code: string): Stakeholder | undefined {
    return this.data.stakeholders.find(s => s.referralCode === code);
  }

  createStakeholder(data: Omit<Stakeholder, 'id' | 'referralCode' | 'referralLink' | 'qrUrl' | 'createdAt' | 'updatedAt'>): Stakeholder {
    const now = new Date().toISOString();
    const referralCode = generateReferralCode('SH');
    const stakeholder: Stakeholder = {
      ...data,
      id: this.generateId(),
      referralCode,
      referralLink: buildReferralLink(referralCode),
      qrUrl: generateQRPlaceholderUrl(referralCode),
      createdAt: now,
      updatedAt: now,
    };
    this.data.stakeholders.push(stakeholder);
    this.saveToStorage();
    return stakeholder;
  }

  updateStakeholder(id: string, data: Partial<Omit<Stakeholder, 'id' | 'createdAt'>>): Stakeholder | null {
    const index = this.data.stakeholders.findIndex(s => s.id === id);
    if (index === -1) return null;

    this.data.stakeholders[index] = {
      ...this.data.stakeholders[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    this.saveToStorage();
    return this.data.stakeholders[index];
  }

  getReferrals(): Referral[] {
    return [...this.data.referrals];
  }

  getReferral(id: string): Referral | undefined {
    return this.data.referrals.find(r => r.id === id);
  }

  getReferralsByType(type: 'in' | 'out'): Referral[] {
    return this.data.referrals.filter(r => r.type === type);
  }

  getReferralsByReferrer(referrerId: string): Referral[] {
    return this.data.referrals.filter(r => r.source.id === referrerId);
  }

  createReferral(data: Omit<Referral, 'id' | 'dateCreated'>): Referral {
    const referral: Referral = {
      ...data,
      id: this.generateId(),
      dateCreated: new Date().toISOString(),
    };
    this.data.referrals.push(referral);
    this.saveToStorage();
    return referral;
  }

  updateReferral(id: string, data: Partial<Omit<Referral, 'id' | 'dateCreated'>>): Referral | null {
    const index = this.data.referrals.findIndex(r => r.id === id);
    if (index === -1) return null;

    this.data.referrals[index] = {
      ...this.data.referrals[index],
      ...data,
    };
    this.saveToStorage();
    return this.data.referrals[index];
  }

  getCommissionRules(): CommissionRule[] {
    return [...this.data.commissionRules];
  }

  getCommissionRule(id: string): CommissionRule | undefined {
    return this.data.commissionRules.find(r => r.id === id);
  }

  getCommissionRuleByReferrer(referrerId: string): CommissionRule | undefined {
    return this.data.commissionRules.find(r => r.referrer.id === referrerId && r.active);
  }

  createCommissionRule(data: Omit<CommissionRule, 'id' | 'createdAt' | 'updatedAt'>): CommissionRule {
    const now = new Date().toISOString();
    const rule: CommissionRule = {
      ...data,
      id: this.generateId(),
      createdAt: now,
      updatedAt: now,
    };
    this.data.commissionRules.push(rule);
    this.saveToStorage();
    return rule;
  }

  updateCommissionRule(id: string, data: Partial<Omit<CommissionRule, 'id' | 'createdAt'>>): CommissionRule | null {
    const index = this.data.commissionRules.findIndex(r => r.id === id);
    if (index === -1) return null;

    this.data.commissionRules[index] = {
      ...this.data.commissionRules[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    this.saveToStorage();
    return this.data.commissionRules[index];
  }

  getCommissionEntries(): CommissionEntry[] {
    return [...this.data.commissionEntries];
  }

  getCommissionEntry(id: string): CommissionEntry | undefined {
    return this.data.commissionEntries.find(e => e.id === id);
  }

  getCommissionEntriesByReferrer(referrerId: string): CommissionEntry[] {
    return this.data.commissionEntries.filter(e => e.referrer.id === referrerId);
  }

  getUnpaidCommissionEntries(): CommissionEntry[] {
    return this.data.commissionEntries.filter(e => e.payoutStatus === 'unpaid');
  }

  createCommissionEntry(data: Omit<CommissionEntry, 'id' | 'createdAt' | 'updatedAt'>): CommissionEntry {
    const now = new Date().toISOString();
    const entry: CommissionEntry = {
      ...data,
      id: this.generateId(),
      createdAt: now,
      updatedAt: now,
    };
    this.data.commissionEntries.push(entry);
    this.saveToStorage();
    return entry;
  }

  updateCommissionEntry(id: string, data: Partial<Omit<CommissionEntry, 'id' | 'createdAt'>>): CommissionEntry | null {
    const index = this.data.commissionEntries.findIndex(e => e.id === id);
    if (index === -1) return null;

    this.data.commissionEntries[index] = {
      ...this.data.commissionEntries[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    this.saveToStorage();
    return this.data.commissionEntries[index];
  }

  getPayouts(): Payout[] {
    return [...this.data.payouts];
  }

  getPayout(id: string): Payout | undefined {
    return this.data.payouts.find(p => p.id === id);
  }

  getPayoutsByReferrer(referrerId: string): Payout[] {
    return this.data.payouts.filter(p => p.referrer.id === referrerId);
  }

  createPayout(data: Omit<Payout, 'id'>): Payout {
    const payout: Payout = {
      ...data,
      id: this.generateId(),
    };
    this.data.payouts.push(payout);
    this.saveToStorage();
    return payout;
  }

  getPolicy(): ReferralPolicy | undefined {
    return this.data.policies[0];
  }

  updatePolicy(data: Partial<Omit<ReferralPolicy, 'id' | 'createdAt'>>): ReferralPolicy | null {
    if (this.data.policies.length === 0) return null;

    this.data.policies[0] = {
      ...this.data.policies[0],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    this.saveToStorage();
    return this.data.policies[0];
  }

  resolveReferrerByCode(code: string): { kind: string; entity: RefPartner | Staff | Stakeholder } | null {
    const partner = this.getPartnerByCode(code);
    if (partner) return { kind: 'external_partner', entity: partner };

    const staff = this.getStaffByCode(code);
    if (staff) return { kind: 'internal_staff', entity: staff };

    const stakeholder = this.getStakeholderByCode(code);
    if (stakeholder) return { kind: 'stakeholder', entity: stakeholder };

    return null;
  }
}

export const mockReferralDB = new MockReferralDB();
